import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase } from '../lib/typeHelper'

export interface AircallMe {
  integrationEnabled: boolean
  linked: boolean
  aircallUserId: number | null
  aircallEmail: string | null
  status: 'unlinked' | 'active' | 'disabled'
  smsEnabled: boolean
  smsFromNumber: string | null
}

function getAircallMe(): Promise<AircallMe> {
  return axios.get('/api/v1/insure/aircall/me/').then((response) => toCamelCase(response.data) as AircallMe)
}

export function useAircallMe() {
  return useQuery({
    queryKey: ['AIRCALL_ME'],
    queryFn: getAircallMe,
  })
}

export interface LinkAircallIdentityPayload {
  aircallUserId: number
  email: string
}

export interface AircallIdentityResult {
  linked: boolean
  aircallEmail: string
}

function linkAircallIdentity(payload: LinkAircallIdentityPayload): Promise<AircallIdentityResult> {
  return axios
    .post('/api/v1/insure/aircall/identity/', {
      aircall_user_id: payload.aircallUserId,
      email: payload.email,
    })
    .then((response) => toCamelCase(response.data) as AircallIdentityResult)
}

export function useLinkAircallIdentity() {
  return useMutation({ mutationFn: linkAircallIdentity })
}

export interface DialAircallPayload {
  to: string
  leadId?: string
  contactId?: string
}

export interface AircallDialResult {
  hintId: string
  expiresAt: string
}

function dialAircall(payload: DialAircallPayload): Promise<AircallDialResult> {
  return axios
    .post('/api/v1/insure/aircall/dial/', {
      to: payload.to,
      lead_id: payload.leadId,
      contact_id: payload.contactId,
    })
    .then((response) => toCamelCase(response.data) as AircallDialResult)
}

export function useDialAircall() {
  return useMutation({ mutationFn: dialAircall })
}

export interface ActivityTranscriptResult {
  transcript: string
}

function getActivityTranscript(activityId: string): Promise<ActivityTranscriptResult> {
  return axios
    .get(`/api/v1/insure/aircall/activities/${activityId}/transcript/`)
    .then((response) => toCamelCase(response.data) as ActivityTranscriptResult)
}

// Each fetch writes a CallContentAccessLog row server-side, so we lazy-fetch
// (the user has to click "Show transcript") and don't cache across mounts.
export function useActivityTranscript(activityId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['AIRCALL_TRANSCRIPT', activityId],
    queryFn: () => getActivityTranscript(activityId),
    enabled,
    staleTime: 0,
    gcTime: 0,
  })
}

export interface RecordingUrlResult {
  url: string
}

function getRecordingUrl(activityId: string): Promise<RecordingUrlResult> {
  return axios
    .get(`/api/v1/insure/aircall/recordings/${activityId}/`)
    .then((response) => toCamelCase(response.data) as RecordingUrlResult)
}

// The signed URL is valid ~4h server-side. Cache it for 3h (a margin before
// expiry) so re-renders and remounts reuse it instead of re-fetching — each
// fetch writes a CallContentAccessLog row. gcTime matches staleTime so the
// entry survives remounts for the full window; the default 5m gcTime would
// otherwise drop it and force a re-fetch (and a new access-log row).
const RECORDING_URL_CACHE_MS = 3 * 60 * 60 * 1000

export function useRecordingUrl(activityId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['AIRCALL_RECORDING_URL', activityId],
    queryFn: () => getRecordingUrl(activityId),
    enabled,
    staleTime: RECORDING_URL_CACHE_MS,
    gcTime: RECORDING_URL_CACHE_MS,
  })
}

export interface SendSmsPayload {
  to: string
  body: string
  leadId?: string
}

export interface SendSmsResult {
  id: string
  status: string
  to: string
  from: string
  body: string
  aircallMessageId: string | null
  createdAt: string
}

function sendSms(payload: SendSmsPayload): Promise<SendSmsResult> {
  return axios
    .post('/api/v1/insure/aircall/sms/send/', {
      to: payload.to,
      body: payload.body,
      lead_id: payload.leadId,
    })
    .then((response) => toCamelCase(response.data) as SendSmsResult)
}

export function useSendSms(leadId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: sendSms,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS, leadId] })
    },
  })
}

function detachCallActivity(activityId: string): Promise<{ detached: boolean }> {
  return axios.delete(`/api/v1/insure/aircall/activities/${activityId}/`).then((response) => response.data)
}

export function useDetachCallActivity(leadId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: detachCallActivity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS, leadId] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.STAFF_USER_ACTIVITY] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.STAFF_USER] })
    },
  })
}
